import { Link } from "react-router-dom";
import { PageMeta } from "@/components/PageMeta";
import { canonicalUrl } from "@/lib/canonical";
import FieldHeader from "@/components/field/FieldHeader";
import FieldFooter from "@/components/field/FieldFooter";
import "@/styles/design-tokens.css";
import "@/components/docs/docs.css";
import "../shl.css";

export default function ShlQuickstartPage() {
  return (
    <div className="ffly ffly--public shl-doc">
      <PageMeta
        title="SMART Health Links SDK Quickstart"
        description="Build an International Patient Summary, encrypt it as a SMART Health Link, and generate a QR code in under 20 lines of code with the FHIRfly SHL SDK."
        canonical={canonicalUrl("/shl/quickstart")}
      />

      <FieldHeader />

      <div className="wrap">
        {/* Intro */}
        <div className="crumb">SDK · SMART Health Links</div>
        <h1 className="docs-h1">SMART Health Links SDK</h1>
        <p className="lede">
          Build an International Patient Summary, encrypt it as a SMART Health Link,
          and generate a QR code &mdash; in under 20 lines of code.
        </p>

        {/* ── Install ── */}
        <hr className="docs-hr" />
        <h2 className="docs-h2">Installation</h2>
        <div className="docs-pre-wrapper">
          <pre className="docs-pre">
            <code>{`npm install @fhirfly-io/shl`}</code>
          </pre>
        </div>
        <p className="docs-p">
          If using FHIRfly API enrichment (recommended &mdash; adds display names and SNOMED
          cross-mappings automatically):
        </p>
        <div className="docs-pre-wrapper">
          <pre className="docs-pre">
            <code>{`npm install @fhirfly-io/shl @fhirfly-io/terminology`}</code>
          </pre>
        </div>

        {/* ── Step 1 ── */}
        <hr className="docs-hr" />
        <h2 className="docs-h2">1. Build an IPS Bundle</h2>
        <div className="docs-pre-wrapper">
          <pre className="docs-pre">
            <code>{`import { IPS, SHL } from "@fhirfly-io/shl";
import Fhirfly from "@fhirfly-io/terminology";

const client = new Fhirfly({ apiKey: process.env.FHIRFLY_API_KEY });

const bundle = new IPS.Bundle({
  given: "Maria",
  family: "Garcia",
  birthDate: "1985-03-15",
  gender: "female",
});

// Add clinical data (FHIRfly enriches with SNOMED, display names, etc.)
bundle.addMedication({ byNDC: "00071015523", fhirfly: client.ndc });
bundle.addCondition({ byICD10: "E11.9", fhirfly: client.icd10 });
bundle.addAllergy({ bySNOMED: "387207008" });
bundle.addImmunization({ byCVX: "208", fhirfly: client.cvx });
bundle.addResult({
  byLOINC: "2339-0",
  fhirfly: client.loinc,
  value: 95,
  unit: "mg/dL",
});

const fhirBundle = await bundle.build();`}</code>
          </pre>
        </div>
        <p className="docs-p">
          Each <code className="docs-code-inline">add*</code> method accepts multiple input
          formats: code-based with enrichment (<code className="docs-code-inline">byNDC</code>,{" "}
          <code className="docs-code-inline">byICD10</code>,{" "}
          <code className="docs-code-inline">byCVX</code>,{" "}
          <code className="docs-code-inline">byLOINC</code>),
          SNOMED direct (<code className="docs-code-inline">bySNOMED</code>),
          existing FHIR resources (<code className="docs-code-inline">fromResource</code>),
          or manual <code className="docs-code-inline">code</code>/<code className="docs-code-inline">system</code>/<code className="docs-code-inline">display</code>.
        </p>
        <div className="shl-callout">
          <p>
            <strong>No API key?</strong> Use{" "}
            <code className="docs-code-inline">bySNOMED</code> (no API call needed) or
            manual <code className="docs-code-inline">code</code>/<code className="docs-code-inline">system</code>/<code className="docs-code-inline">display</code> input.
            The FHIRfly API adds display names and SNOMED cross-mappings but isn&apos;t required.
          </p>
        </div>

        {/* ── Step 2 ── */}
        <hr className="docs-hr" />
        <h2 className="docs-h2">2. Create an SHL</h2>
        <div className="docs-pre-wrapper">
          <pre className="docs-pre">
            <code>{`// Option A: FHIRfly hosted (zero infrastructure)
const storage = new SHL.FhirflyStorage({
  apiKey: process.env.FHIRFLY_API_KEY,
});

// Option B: Local storage (for development)
// const storage = new SHL.LocalStorage({
//   directory: "./shl-data",
//   baseUrl: "http://localhost:3456/shl",
// });

const result = await SHL.create({
  bundle: fhirBundle,
  storage,
  passcode: "1234",
  label: "Maria's Health Summary",
});

console.log(result.url);     // shlink:/eyJ...
console.log(result.qrCode);  // data:image/png;base64,...`}</code>
          </pre>
        </div>
        <p className="docs-p">This does four things:</p>
        <ol className="docs-ol">
          <li className="docs-li">Generates a random 256-bit encryption key and SHL ID</li>
          <li className="docs-li">Encrypts the FHIR bundle as a JWE (AES-256-GCM, DEFLATE compression)</li>
          <li className="docs-li">Stores three files: <code className="docs-code-inline">content.jwe</code>, <code className="docs-code-inline">manifest.json</code>, <code className="docs-code-inline">metadata.json</code></li>
          <li className="docs-li">Builds the <code className="docs-code-inline">shlink:/</code> URL (with the key embedded) and a QR code PNG</li>
        </ol>
        <div className="shl-callout">
          <p>
            <strong>FhirflyStorage</strong> is included free in all plans with per-plan limits on
            active SHLs and attachments. The decryption key is never sent to FHIRfly &mdash; only
            opaque encrypted blobs are stored.
            See <Link to="/pricing">Pricing</Link> and{" "}
            <Link to="/docs/shl/storage-adapters">Storage Adapters</Link> for details.
          </p>
        </div>

        {/* ── Step 3 ── */}
        <hr className="docs-hr" />
        <h2 className="docs-h2">3. Share</h2>
        <p className="docs-p">
          The <code className="docs-code-inline">result.url</code> is a complete SMART Health Link.
          Share it by:
        </p>
        <ul className="docs-ul">
          <li className="docs-li">
            <strong>QR Code</strong> &mdash; Save{" "}
            <code className="docs-code-inline">result.qrCode</code> as a PNG image for scanning
          </li>
          <li className="docs-li">
            <strong>Deep Link</strong> &mdash;{" "}
            <code className="docs-code-inline">https://fhirfly.io/shl/viewer#shlink:/eyJ...</code>
          </li>
          <li className="docs-li">
            <strong>Text</strong> &mdash; Send the{" "}
            <code className="docs-code-inline">shlink:/</code> URL directly
          </li>
        </ul>

        {/* ── Step 4 ── */}
        <hr className="docs-hr" />
        <h2 className="docs-h2">4. View</h2>
        <p className="docs-p">
          Open the{" "}
          <Link to="/shl/viewer" className="docs-a">SHL Viewer</Link>{" "}
          and paste the link, or use the deep link format above. All decryption happens in
          the browser &mdash; the encryption key never leaves the page.
        </p>

        {/* ── CLI ── */}
        <hr className="docs-hr" />
        <h2 className="docs-h2">CLI alternative</h2>
        <p className="docs-p">For quick testing without writing code:</p>
        <div className="docs-pre-wrapper">
          <pre className="docs-pre">
            <code>{`# Full demo with sample patient data
npx @fhirfly-io/shl demo

# Create from an existing FHIR Bundle file
npx @fhirfly-io/shl create my-bundle.json --passcode 1234

# Start a local server
npx @fhirfly-io/shl serve --dir ./shl-data --port 3456

# Validate a FHIR Bundle against IPS
npx @fhirfly-io/shl validate bundle.json

# Decode an SHL URL
npx @fhirfly-io/shl decode shlink:/eyJ...`}</code>
          </pre>
        </div>

        {/* ── Security ── */}
        <hr className="docs-hr" />
        <h2 className="docs-h2">How encryption works</h2>
        <p className="docs-p">
          The SDK uses a <strong>zero-knowledge architecture</strong>. The encryption key
          lives only in the <code className="docs-code-inline">shlink:/</code> URL &mdash; it
          is never sent to the server. The server stores and serves opaque encrypted blobs.
          Only someone with the URL (or QR code) can decrypt the data.
        </p>
        <div className="docs-table-container">
          <table className="docs-table">
            <thead className="docs-thead">
              <tr>
                <th className="docs-th">Data</th>
                <th className="docs-th">Where it lives</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="docs-td">Patient health data</td>
                <td className="docs-td">Encrypted (AES-256-GCM) on your storage backend</td>
              </tr>
              <tr>
                <td className="docs-td">Decryption key</td>
                <td className="docs-td">Embedded in the <code className="docs-code-inline">shlink:/</code> URL only</td>
              </tr>
              <tr>
                <td className="docs-td">Passcode</td>
                <td className="docs-td">SHA-256 hashed in server metadata</td>
              </tr>
              <tr>
                <td className="docs-td">Access controls</td>
                <td className="docs-td">Server-side metadata (expiration, max accesses)</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* ── Next steps ── */}
        <hr className="docs-hr" />
        <h2 className="docs-h2">Next steps</h2>
        <div className="shl-next">
          <NextStepLink href="/docs/shl/ehr-integration" title="EHR Integration Guide" description="Map HL7v2, CCDA, FHIR R4, and flat database records to IPS bundles" />
          <NextStepLink href="/docs/shl/server-guide" title="Server Guide" description="Self-host SHL endpoints with Express, Fastify, or Lambda" />
          <NextStepLink href="/docs/shl/storage-adapters" title="Storage Adapters" description="Compare FhirflyStorage, S3, Azure, GCS, and local filesystem" />
          <NextStepLink href="/docs/shl/security" title="Security & Compliance" description="Zero-knowledge architecture, HIPAA, compliance checklist" />
          <NextStepLink href="/docs/shl/cli-reference" title="CLI Reference" description="All CLI commands and options" />
          <NextStepLink href="/docs/shl/troubleshooting" title="Troubleshooting" description="Common issues and patient input format guide" />
        </div>
      </div>

      <FieldFooter variant="full" />
    </div>
  );
}

function NextStepLink({ href, title, description }: {
  href: string;
  title: string;
  description: string;
}) {
  return (
    <Link to={href}>
      <i className="arr fa-solid fa-arrow-right" aria-hidden="true" />
      <span>
        <span className="t">{title}</span>{" "}
        <span className="d">&mdash; {description}</span>
      </span>
    </Link>
  );
}
